﻿import { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiChevronRight } from 'react-icons/fi';
import { useProspects, type Prospect } from '@/hooks/useProspects';
import { useCompanies } from '@/hooks/useCompanies';
import { useToast } from '@/components/common/Toast';
import PageHeader from '@/components/common/PageHeader';
import Spinner from '@/components/common/Spinner';
import CompanySelect from '@/components/common/CompanySelect';
import FilterChips from '@/components/common/FilterChips';
import ConfirmModal from '@/components/common/ConfirmModal';
import NewProspectModal from '@/components/visits/NewProspectModal';
import ProspectDetailPanel from '@/components/prospects/ProspectDetailPanel';

export default function ProspectListPage() {
  const { t } = useTranslation();
  const { prospects, loading, fetchProspects } = useProspects();
  const { companies } = useCompanies();
  const { showToast } = useToast();
  const [codazienda, setCodazienda] = useState('0');
  const [deleteTarget, setDeleteTarget] = useState<Prospect | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [detailProspect, setDetailProspect] = useState<Prospect | null>(null);
  const isFirstRender = useRef(true);

  // Auto-search when azienda changes
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (codazienda !== '0') fetchProspects(codazienda);
  }, [codazienda]);

  const getAziendaLabel = () => {
    const c = companies.find(c => c.codice === codazienda);
    return c ? c.descrizione : codazienda;
  };

  const chips = [];
  if (codazienda !== '0') chips.push({ key: 'codazienda', label: t('labels.company'), value: codazienda, displayValue: getAziendaLabel() });

  const handleRemove = () => { setCodazienda('0'); };
  const handleClearAll = () => { setCodazienda('0'); };

  const handleProspectSaved = (updated?: any) => {
    setDetailProspect(null);
    if (updated) {
      const idx = prospects.findIndex(p => p.codcliente === updated.codcliente);
      if (idx >= 0) prospects[idx] = { ...prospects[idx], ...updated };
    }
  };

  return (
    <>
      <PageHeader title={t('nav.prospectList')} />
      <div className="container py-3">
        <Card className="mb-3 card-filters">
          <Card.Body className="py-2">
            <Row className="align-items-end g-2">
              <Col md={4}>
                <CompanySelect value={codazienda} onChange={setCodazienda} />
              </Col>
              <Col md={3}>
                <Button variant="outline-success" size="sm" className="w-100" onClick={() => setShowNewModal(true)}>
                  <FiPlus className="me-1" /> Crea nuovo Prospect
                </Button>
              </Col>
            </Row>
          </Card.Body>
          {chips.length > 0 && (
            <Card.Footer className="py-2">
              <FilterChips chips={chips} onRemove={handleRemove} onClearAll={handleClearAll} />
            </Card.Footer>
          )}
        </Card>

        {loading ? <Spinner /> : (
          <table className="table-modern">
            <thead>
              <tr>
                <th>Prospect</th>
                <th>Indirizzo</th>
                <th>Contatti</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {prospects.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted py-3">{t('labels.noResults')}</td></tr>
              ) : prospects.map((p, i) => (
                <tr key={p.codcliente || i}>
                  <td data-label="Prospect">
                    <small className="text-muted" style={{ fontSize: 10 }}>[{p.codcliente}]</small>{' '}
                    <strong style={{ fontSize: 13 }}>{p.ragsociale}</strong>
                  </td>
                  <td data-label="Indirizzo" style={{ fontSize: 12 }}>
                    {[p.indirizzo, p.localita, p.prov].filter(Boolean).join(', ')}
                  </td>
                  <td data-label="Contatti" style={{ fontSize: 12 }}>
                    {p.telefono && <div>{p.telefono}</div>}
                    {p.email && <div>{p.email}</div>}
                  </td>
                  <td>
                    <span className="d-flex align-items-center justify-content-end gap-1">
                      <Button size="sm" variant="outline-secondary" className="btn-icon"
                        onClick={() => setDetailProspect(p)}>
                        <FiChevronRight />
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ProspectDetailPanel
        prospect={detailProspect}
        show={!!detailProspect}
        onHide={() => setDetailProspect(null)}
        onSaved={handleProspectSaved}
      />

      <NewProspectModal
        show={showNewModal}
        preselectedAzienda={codazienda !== '0' ? codazienda : undefined}
        onClose={() => setShowNewModal(false)}
        onSaved={() => { setShowNewModal(false); if (codazienda !== '0') fetchProspects(codazienda); }}
      />

      <ConfirmModal
        show={!!deleteTarget}
        message="Sei sicuro di voler eliminare questo prospect?"
        onConfirm={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

